import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { AppState } from "@/store";
import styles from "../../styles/shap_of_you.module.scss";
import { FilterViewScreenProps, FilterState } from "./types";

const FilterViewScreen: React.FC<FilterViewScreenProps> = ({
  navigateToStep,
  filter,
  setFilter,
  activeTab,
}) => {
  const [selectedTab, setSelectedTab] = useState<
    "Price" | "Purity" | "Occassion"
  >("Price");
  const [filterSheet, setFilterSheet] = useState<FilterState>({
    metal: [],
    occasions: [],
    priceRange: null,
    priceMin: null,
    priceMax: null,
  });

  const categoryState = useSelector((state: AppState) => state.category);

  // Price options matching Product.tsx
  const priceOptions = [
    { label: "Below ₹ 10,000", min: null, max: 10000 },
    { label: "₹ 10,000 – ₹ 20,000", min: 10000, max: 20000 },
    { label: "₹ 30,000 – ₹ 40,000", min: 30000, max: 40000 },
    { label: "₹ 40,000 – ₹ 50,000", min: 40000, max: 50000 },
    { label: "₹ 50,000 – ₹ 1,00,000", min: 50000, max: 100000 },
  ];

  // Sync filterSheet with filter when component mounts or filter changes
  useEffect(() => {
    setFilterSheet({
      metal: filter.metal || [],
      occasions: filter.occasions || [],
      priceRange: filter.priceRange || null,
      priceMin: filter.priceMin || null,
      priceMax: filter.priceMax || null,
    });
  }, [filter]);

  // Handle filter changes in the sheet (temporary state)
  const handleSheetFilterChange = (filterType: string, value: any) => {
    setFilterSheet((prev: any) => {
      if (filterType === "price") {
        const label = value.label;
        const currentSelected: string[] = prev.priceRange
          ? prev.priceRange.split("|")
          : [];
        const newSelected = currentSelected.includes(label)
          ? currentSelected.filter((r: string) => r !== label)
          : [...currentSelected, label];

        // Compute merged min/max from all selected ranges
        const selectedOptions = priceOptions.filter((opt) =>
          newSelected.includes(opt.label)
        );
        const hasNullMin = selectedOptions.some((opt) => opt.min === null);
        const mins = selectedOptions
          .map((opt) => opt.min)
          .filter((m): m is number => m !== null);
        const maxes = selectedOptions.map((opt) => opt.max);

        const priceMin =
          selectedOptions.length === 0
            ? null
            : hasNullMin
            ? null
            : mins.length > 0
            ? Math.min(...mins)
            : null;
        const priceMax = maxes.length > 0 ? Math.max(...maxes) : null;

        return {
          ...prev,
          priceRange: newSelected.length > 0 ? newSelected.join("|") : null,
          priceMin,
          priceMax,
        };
      } else if (filterType === "metal") {
        // For purity filter
        const metals = prev?.metal || [];
        if (metals.includes(value)) {
          return { ...prev, metal: metals.filter((m: any) => m !== value) };
        } else {
          return { ...prev, metal: [...metals, value] };
        }
      } else if (filterType === "occasion") {
        // For occasion filter
        const occasions = prev?.occasions || [];
        if (occasions.includes(value)) {
          return {
            ...prev,
            occasions: occasions.filter((m: any) => m !== value),
          };
        } else {
          return { ...prev, occasions: [...occasions, value] };
        }
      }
      return prev;
    });
  };

  // Apply filters and navigate back
  const handleApplyFilter = () => {
    setFilter(filterSheet);
    navigateToStep("productView");
  };

  // Clear all filters
  const handleClearFilters = () => {
    const clearedSheet = {
      metal: [],
      occasions: [],
      priceRange: null,
      priceMin: null,
      priceMax: null,
    };
    setFilterSheet(clearedSheet);
  };

  const handleBackToProduct = () => {
    navigateToStep("productView");
  };

  // Check icon component
  const CheckIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20 6L9 17L4 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  return (
    <div className="d-md-none w-100">
      <div className={styles.filterWrapper}>
        {/* Header */}
        <div className="w-100">
          <div className={styles.header}>
            <h2>
              {" "}
              <span
                className={styles.backArrow}
                onClick={handleBackToProduct}
              >
                ←
              </span>
              <svg
                style={{ margin: "0 10px" }}
                width="30"
                height="30"
                viewBox="0 0 36 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 15C6 15.7956 6.31607 16.5587 6.87868 17.1213C7.44129 17.6839 8.20435 18 9 18C9.79565 18 10.5587 17.6839 11.1213 17.1213C11.6839 16.5587 12 15.7956 12 15C12 14.2044 11.6839 13.4413 11.1213 12.8787C10.5587 12.3161 9.79565 12 9 12C8.20435 12 7.44129 12.3161 6.87868 12.8787C6.31607 13.4413 6 14.2044 6 15Z"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 6V12"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9 18V30"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 24C15 24.7956 15.3161 25.5587 15.8787 26.1213C16.4413 26.6839 17.2044 27 18 27C18.7956 27 19.5587 26.6839 20.1213 26.1213C20.6839 25.5587 21 24.7956 21 24C21 23.2044 20.6839 22.4413 20.1213 21.8787C19.5587 21.3161 18.7956 21 18 21C17.2044 21 16.4413 21.3161 15.8787 21.8787C15.3161 22.4413 15 23.2044 15 24Z"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 6V21"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M18 27V30"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M24 10.5C24 11.2956 24.3161 12.0587 24.8787 12.6213C25.4413 13.1839 26.2044 13.5 27 13.5C27.7956 13.5 28.5587 13.1839 29.1213 12.6213C29.6839 12.0587 30 11.2956 30 10.5C30 9.70435 29.6839 8.94129 29.1213 8.37868C28.5587 7.81607 27.7956 7.5 27 7.5C26.2044 7.5 25.4413 7.81607 24.8787 8.37868C24.3161 8.94129 24 9.70435 24 10.5Z"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M27 6V7.5"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M27 13.5V30"
                  stroke="#262121"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Filter By
            </h2>
          </div>

          {/* Tabs */}
          <div className={styles.tabs}>
            <button
              className={`${styles.tab} ${
                selectedTab === "Price" ? styles.active : ""
              }`}
              onClick={() => setSelectedTab("Price")}
            >
              Price
            </button>

            <button
              className={`${styles.tab} ${
                selectedTab === "Purity" ? styles.active : ""
              }`}
              onClick={() => setSelectedTab("Purity")}
            >
              Purity
            </button>

            <button
              className={`${styles.tab} ${
                selectedTab === "Occassion" ? styles.active : ""
              }`}
              onClick={() => setSelectedTab("Occassion")}
            >
              Occassion
            </button>
          </div>

          {/* Tab Content */}
          <div className={styles.content}>
            {selectedTab === "Price" && (
              <div className={styles.options}>
                {priceOptions.map((option) => {
                  const selectedRanges = filterSheet?.priceRange
                    ? filterSheet.priceRange.split("|")
                    : [];
                  const isChecked = selectedRanges.includes(option.label);
                  return (
                    <label key={option.label} className={styles.optionItem}>
                      <span
                        style={{
                          fontWeight: isChecked ? "bold" : "normal",
                        }}
                      >
                        {option.label}
                      </span>
                      <input
                        type="checkbox"
                        name="price"
                        checked={isChecked}
                        onChange={() =>
                          handleSheetFilterChange("price", option)
                        }
                        style={{ display: "none" }}
                      />
                      <span
                        className={styles.checkbox}
                        style={{
                          borderRadius: "50%",
                          border: "1px solid #ccc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "16px",
                          height: "16px",
                        }}
                      >
                        {isChecked && <CheckIcon />}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}

            {selectedTab === "Purity" && (
              <div className={styles.options}>
                {categoryState.similarFilters?.purities?.map(
                  (purity: string, index: number) => {
                    const isChecked =
                      filterSheet?.metal?.includes(purity) || false;
                    return (
                      <label key={index} className={styles.optionItem}>
                        <span
                          style={{
                            fontWeight: isChecked ? "bold" : "normal",
                          }}
                        >
                          {purity}
                        </span>
                        <input
                          type="checkbox"
                          name="purity"
                          checked={isChecked}
                          onChange={() =>
                            handleSheetFilterChange("metal", purity)
                          }
                          style={{ display: "none" }}
                        />
                        <span
                          className={styles.checkbox}
                          style={{
                            borderRadius: "50%",
                            border: "1px solid #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "16px",
                            height: "16px",
                          }}
                        >
                          {isChecked && <CheckIcon />}
                        </span>
                      </label>
                    );
                  }
                )}
                {(!categoryState.similarFilters?.purities ||
                  categoryState.similarFilters?.purities.length === 0) && (
                  <div className={styles.placeholder}>
                    No purity options available for {activeTab}
                  </div>
                )}
              </div>
            )}

            {selectedTab === "Occassion" && (
              <div className={styles.options}>
                {categoryState.similarFilters?.occasions?.map(
                  (occasion: any, index: number) => {
                    const isChecked =
                      filterSheet?.occasions?.includes(occasion.name) || false;
                    return (
                      <label key={index} className={styles.optionItem}>
                        <span
                          style={{
                            fontWeight: isChecked ? "bold" : "normal",
                          }}
                        >
                          {occasion.name}
                        </span>
                        <input
                          type="checkbox"
                          name="occasion"
                          checked={isChecked}
                          onChange={() =>
                            handleSheetFilterChange("occasion", occasion.name)
                          }
                          style={{ display: "none" }}
                        />
                        <span
                          className={styles.checkbox}
                          style={{
                            borderRadius: "50%",
                            border: "1px solid #ccc",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "16px",
                            height: "16px",
                          }}
                        >
                          {isChecked && <CheckIcon />}
                        </span>
                      </label>
                    );
                  }
                )}
                {(!categoryState.similarFilters?.occasions ||
                  categoryState.similarFilters?.occasions.length === 0) && (
                  <div className={styles.placeholder}>
                    No occasion options available for {activeTab}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.clearBtn} onClick={handleClearFilters}>
            Clear Selection
          </button>

          <button className={styles.applyBtn} onClick={handleApplyFilter}>
            <svg
              style={{ marginRight: "10px" }}
              width="17"
              height="13"
              viewBox="0 0 17 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.2637 1.41406L6.36328 11.3135L0.707031 5.65625L1.41406 4.94922L6.36426 9.89941L15.5566 0.707031L16.2637 1.41406Z"
                fill="#262121"
                stroke="white"
              />
            </svg>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterViewScreen;
